import type { ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { EntitlementDeniedState, LoadingState } from "@odingard/design-system";
import { useAuth } from "../lib/auth";
import { useAsync } from "../lib/useAsync";
import { api } from "../lib/api";

export const GOVERNANCE_ENTITLEMENTS = [
  "policy_ai.enabled",
  "policy_ai.governance_compliance.enabled",
] as const;

const ACTIVE = ["ALLOWED", "TRIAL"];

export function hasGovernanceEntitlements(
  entitlements: { key: string; status: string }[] | null | undefined,
): boolean {
  if (!entitlements) return false;
  return GOVERNANCE_ENTITLEMENTS.every((key) =>
    entitlements.some((e) => e.key === key && ACTIVE.includes(e.status)),
  );
}

/**
 * Fail-closed route guard for Governance & Compliance (APP-004 R3).
 *
 * Requires BOTH `policy_ai.enabled` and
 * `policy_ai.governance_compliance.enabled`: a Policy AI subscription alone
 * does not include governance. The Worker and the Policy AI backend both
 * re-check server-side; this only keeps unentitled users out of the UI.
 */
export function GovernanceGuard({ children }: { children: ReactNode }) {
  const { activeOrg } = useAuth();
  const navigate = useNavigate();
  const orgId = activeOrg?.id ?? "";
  const state = useAsync(async () => (await api.entitlements(orgId)).entitlements, [orgId]);

  if (state.loading) return <LoadingState label="Checking your access…" />;
  if (!hasGovernanceEntitlements(state.data)) {
    return (
      <EntitlementDeniedState product="Governance & Compliance" onUpgrade={() => navigate("/pricing")} />
    );
  }
  return <>{children}</>;
}
